/**
 * Phase 3 — BaselineScenarioPicker
 *
 * Dropdown of the tenant's non-ephemeral scenarios (presets + own).
 * Excludes ROE candidates / quick-sim / matrix-cell rows so the
 * operator only sees real, runnable baselines.
 */
import { useEffect, useState } from "react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import { scrubPhaseLabel } from "@/lib/displayName";
import { Label } from "@/components/ui/label";
import { sim } from "@/api/endpoints";
// Phase 18 Ask 4.4 — pure filter helper lives in its own module so it
// can be unit-tested without pulling in the axios client.
import { isPickable } from "./isPickable";
export { isPickable };

export default function BaselineScenarioPicker({ value, onChange, disabled = false }) {
    const [scenarios, setScenarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const list = await sim.scenarios.list({});
                if (cancelled) return;
                const rows = (Array.isArray(list) ? list : list?.items || [])
                    .filter(isPickable)
                    .sort((a, b) => {
                        // Presets first, then most recently updated.
                        if (a.is_preset !== b.is_preset) return a.is_preset ? -1 : 1;
                        return (b.updated_at || "").localeCompare(a.updated_at || "");
                    });
                setScenarios(rows);
            } catch (e) {
                if (!cancelled) {
                    setErr(formatApiError(e, "Failed to load scenarios"));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className="space-y-1" data-testid="baseline-picker">
            <Label htmlFor="baseline-picker-select">Baseline scenario</Label>
            <Select
                value={value || ""}
                onValueChange={onChange}
                disabled={disabled || loading}
            >
                <SelectTrigger
                    id="baseline-picker-select"
                    data-testid="baseline-picker-trigger"
                    className="bg-ux-bg-1"
                >
                    <SelectValue
                        placeholder={loading ? "Loading scenarios…" : "Pick a scenario"}
                    />
                </SelectTrigger>
                <SelectContent className="bg-ux-bg-1 border-ux-border-1 max-h-[320px]">
                    {scenarios.map((s) => (
                        <SelectItem
                            key={s.id}
                            value={s.id}
                            data-testid={`baseline-picker-option-${s.id}`}
                        >
                            <span className="flex items-center gap-2">
                                <span className="font-medium">{scrubPhaseLabel(s.name)}</span>
                                {s.is_preset && (
                                    <span className="text-[10px] uppercase tracking-wider text-ux-info bg-ux-info-soft px-1.5 py-0.5 rounded">
                                        preset
                                    </span>
                                )}
                                {s.validation_gate_state && (
                                    <span className="text-[10px] text-ux-text-3 ml-auto">
                                        {s.validation_gate_state}
                                    </span>
                                )}
                            </span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {err && (
                <p className="text-[11px] text-ux-danger" data-testid="baseline-picker-error">
                    {err}
                </p>
            )}
            <p className="text-[11px] text-ux-text-3">
                Scenarios marked “preset” are tenant-agnostic; everything else is
                from your tenant. Quick-Sim and matrix-cell scenarios are filtered out.
            </p>
        </div>
    );
}
